/**
 * VC AGENT PLATFORM - MUI THEME CONFIGURATION
 *
 * This theme provides light and dark modes with a distinctive forest green/amber palette
 * that conveys growth, stability, and natural harmony.
 *
 * PRIMARY FONT: Figtree (imported in index.html)
 * BASE FONT SIZE: 0.875rem (14px) - optimized for data-dense interfaces
 *
 * USAGE:
 * ```tsx
 * import { ThemeProvider } from '@mui/material';
 * import { lightTheme, darkTheme, agentStyles, colors } from './theme';
 *
 * // In your root component
 * <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
 *   <App />
 * </ThemeProvider>
 * ```
 *
 * AGENT-SPECIFIC FEATURES:
 * This theme includes special utilities for displaying AI agent status and activity.
 * See the `agentStyles` export at the bottom for animations and styling helpers.
 */

import { createTheme, alpha } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material/styles';

/**
 * COLOR PALETTE
 *
 * Forest Green (Primary): Professional, growth-oriented, trustworthy
 * Amber (Secondary): Warm, energetic, attention-grabbing for CTAs
 * Slate: Neutral grays for backgrounds and text
 * Rose: Error states
 * Emerald: Success states
 *
 * USAGE:
 * Import `colors` to use these values directly in your components:
 * ```tsx
 * import { colors } from './theme';
 * sx={{ backgroundColor: colors.green[500] }}
 * ```
 */
const colors = {
  green: {
    50: '#E8F5F3',
    100: '#C5E7E2',
    200: '#9FD8D0',
    300: '#79C9BE',
    400: '#5CBBAF',
    500: '#3FAD9F',
    600: '#2A8A7E',
    700: '#174a43',
    800: '#123935',
    900: '#0D2827',
  },
  amber: {
    50: '#FFFBEB',
    100: '#FEF3C7',
    200: '#FDE68A',
    300: '#FCD34D',
    400: '#FBBF24',
    500: '#F59E0B',
    600: '#D97706',
    700: '#B45309',
    800: '#92400E',
    900: '#78350F',
  },
  slate: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
  rose: {
    500: '#F43F5E',
    600: '#E11D48',
  },
  emerald: {
    500: '#10B981',
    600: '#059669',
  },
};

/**
 * SHARED THEME OPTIONS
 *
 * Typography hierarchy optimized for data-dense VC interfaces.
 * Base font size: 0.875rem (14px) for better information density.
 *
 * TYPOGRAPHY SCALE:
 * - h1: 2.25rem (36px) - Page titles
 * - h2: 1.875rem (30px) - Section headers
 * - h3: 1.5rem (24px) - Card headers
 * - h4: 1.25rem (20px) - Subsection headers
 * - h5: 1rem (16px) - Component titles
 * - h6: 0.875rem (14px) - Small headers
 * - body1: 0.875rem (14px) - Primary body text
 * - body2: 0.8125rem (13px) - Secondary body text
 * - caption: 0.75rem (12px) - Small annotations
 */
const getSharedTheme = (): ThemeOptions => ({
  typography: {
    fontFamily: '"Halenoir Text", ui-sans-serif, system-ui, sans-serif',
    fontSize: 14, // Base font size in pixels (0.875rem)
    h1: {
      fontWeight: 700,
      fontSize: '2.25rem', // 36px
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.875rem', // 30px
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem', // 24px
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem', // 20px
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1rem', // 16px
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '0.875rem', // 14px
      lineHeight: 1.5,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '0.875rem', // 14px
      lineHeight: 1.5,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.8125rem', // 13px
      lineHeight: 1.57,
    },
    body1: {
      fontSize: '0.875rem', // 14px
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.8125rem', // 13px
      lineHeight: 1.57,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '0.875rem', // 14px
      letterSpacing: '0.01em',
    },
    caption: {
      fontSize: '0.75rem', // 12px
      lineHeight: 1.66,
    },
    overline: {
      fontSize: '0.75rem', // 12px
      fontWeight: 600,
      letterSpacing: '0.5px',
      lineHeight: 2.5,
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

/**
 * LIGHT THEME
 *
 * Primary use case: Daytime work, well-lit environments
 *
 * COLOR DECISIONS:
 * - Primary (Forest Green 700): Main brand color, used for primary actions and navigation
 * - Secondary (Amber 500): Accent color for CTAs and important actions
 * - Background: Soft slate-50 instead of pure white to reduce eye strain
 * - Paper: Pure white for cards and elevated surfaces
 *
 * USAGE:
 * ```tsx
 * import { lightTheme } from './theme';
 * <ThemeProvider theme={lightTheme}>
 * ```
 */
export const lightTheme = createTheme({
  ...getSharedTheme(),
  palette: {
    mode: 'light',
    primary: {
      main: colors.green[700],
      light: colors.green[500],
      dark: colors.green[900],
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: colors.amber[500],
      light: colors.amber[300],
      dark: colors.amber[600],
      contrastText: '#FFFFFF',
    },
    error: {
      main: colors.rose[500],
      dark: colors.rose[600],
    },
    warning: {
      main: colors.amber[500],
      dark: colors.amber[600],
    },
    success: {
      main: colors.emerald[500],
      dark: colors.emerald[600],
    },
    background: {
      default: colors.slate[50],
      paper: '#FFFFFF',
    },
    text: {
      primary: colors.slate[900],
      secondary: colors.slate[600],
      disabled: colors.slate[400],
    },
    divider: colors.slate[200],
    action: {
      active: colors.slate[600],
      hover: alpha(colors.green[700], 0.04),
      selected: alpha(colors.green[700], 0.08),
      disabled: colors.slate[300],
      disabledBackground: colors.slate[100],
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: `${colors.slate[300]} ${colors.slate[100]}`,
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: colors.slate[300],
            minHeight: 24,
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: colors.slate[400],
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            backgroundColor: colors.slate[100],
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: `0 4px 8px ${alpha(colors.green[700], 0.2)}`,
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: `0 1px 3px ${alpha(colors.slate[900], 0.1)}`,
          border: `1px solid ${colors.slate[200]}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: `0 1px 3px ${alpha(colors.slate[900], 0.1)}`,
        },
        elevation2: {
          boxShadow: `0 4px 6px ${alpha(colors.slate[900], 0.1)}`,
        },
        elevation4: {
          boxShadow: `0 10px 15px ${alpha(colors.slate[900], 0.1)}`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: `1px solid ${colors.slate[200]}`,
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${colors.slate[200]}`,
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
        filled: {
          backgroundColor: colors.slate[100],
          color: colors.slate[700],
          '&:hover': {
            backgroundColor: colors.slate[200],
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.slate[200]}`,
        },
        head: {
          fontWeight: 600,
          backgroundColor: colors.slate[50],
          color: colors.slate[700],
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.green[500],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
          },
        },
        notchedOutline: {
          borderWidth: '1px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minHeight: 48,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardSuccess: {
          backgroundColor: alpha(colors.emerald[500], 0.1),
          color: colors.emerald[600],
        },
        standardError: {
          backgroundColor: alpha(colors.rose[500], 0.1),
          color: colors.rose[600],
        },
        standardWarning: {
          backgroundColor: alpha(colors.amber[500], 0.1),
          color: colors.amber[600],
        },
        standardInfo: {
          backgroundColor: alpha(colors.green[500], 0.1),
          color: colors.green[700],
        },
      },
    },
  },
});

/**
 * DARK THEME
 *
 * Primary use case: Evening work, low-light environments, user preference
 *
 * COLOR DECISIONS:
 * - Primary (Forest Green 500): Brighter green that pops against dark backgrounds
 * - Secondary (Amber 300): Softer amber that's readable on dark
 * - Background: True dark (Slate 900) for deep contrast
 * - Paper: Slate 800 for elevated surfaces
 *
 * CONTRAST: All text meets WCAG AA standards for accessibility
 *
 * USAGE:
 * ```tsx
 * import { darkTheme } from './theme';
 * <ThemeProvider theme={darkTheme}>
 * ```
 */
export const darkTheme = createTheme({
  ...getSharedTheme(),
  palette: {
    mode: 'dark',
    primary: {
      main: colors.green[500],
      light: colors.green[400],
      dark: colors.green[700],
      contrastText: colors.slate[900],
    },
    secondary: {
      main: colors.amber[300],
      light: colors.amber[200],
      dark: colors.amber[500],
      contrastText: colors.slate[900],
    },
    error: {
      main: colors.rose[500],
      dark: colors.rose[600],
    },
    warning: {
      main: colors.amber[300],
      dark: colors.amber[500],
    },
    success: {
      main: colors.emerald[500],
      dark: colors.emerald[600],
    },
    background: {
      default: colors.slate[900],
      paper: colors.slate[800],
    },
    text: {
      primary: colors.slate[100],
      secondary: colors.slate[300],
      disabled: colors.slate[500],
    },
    divider: colors.slate[700],
    action: {
      active: colors.slate[300],
      hover: alpha(colors.slate[700], 0.5),
      selected: alpha(colors.green[500], 0.2),
      disabled: colors.slate[600],
      disabledBackground: colors.slate[700],
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: `${colors.slate[600]} ${colors.slate[800]}`,
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: colors.slate[600],
            minHeight: 24,
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: colors.slate[500],
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            backgroundColor: colors.slate[800],
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: `0 4px 8px ${alpha(colors.green[500], 0.3)}`,
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: `0 1px 3px ${alpha('#000000', 0.3)}`,
          border: `1px solid ${colors.slate[700]}`,
          backgroundColor: colors.slate[800],
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: colors.slate[800],
        },
        elevation1: {
          boxShadow: `0 1px 3px ${alpha('#000000', 0.3)}`,
        },
        elevation2: {
          boxShadow: `0 4px 6px ${alpha('#000000', 0.3)}`,
        },
        elevation4: {
          boxShadow: `0 10px 15px ${alpha('#000000', 0.3)}`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: `1px solid ${colors.slate[700]}`,
          backgroundColor: colors.slate[800],
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${colors.slate[700]}`,
          backgroundColor: colors.slate[800],
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
        filled: {
          backgroundColor: colors.slate[700],
          color: colors.slate[200],
          '&:hover': {
            backgroundColor: colors.slate[600],
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.slate[700]}`,
        },
        head: {
          fontWeight: 600,
          backgroundColor: alpha(colors.slate[700], 0.5),
          color: colors.slate[200],
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.green[400],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
          },
        },
        notchedOutline: {
          borderColor: colors.slate[600],
          borderWidth: '1px',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            '& .MuiListItemText-primary': {
              color: '#FFFFFF',
            },
            '& .MuiListItemIcon-root': {
              color: '#FFFFFF',
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minHeight: 48,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardSuccess: {
          backgroundColor: alpha(colors.emerald[500], 0.15),
          color: colors.emerald[500],
        },
        standardError: {
          backgroundColor: alpha(colors.rose[500], 0.15),
          color: colors.rose[500],
        },
        standardWarning: {
          backgroundColor: alpha(colors.amber[300], 0.15),
          color: colors.amber[300],
        },
        standardInfo: {
          backgroundColor: alpha(colors.green[500], 0.15),
          color: colors.green[400],
        },
      },
    },
  },
});

/**
 * AGENT-SPECIFIC STYLES
 *
 * These utilities provide visual feedback for AI agent activity and status.
 * Import and apply these styles to components that display agent state.
 *
 * CRITICAL: These styles make the AI agent activity visible to users.
 * Use them consistently across the platform for coherent UX.
 *
 * ============================================================================
 * 1. AGENT PULSE - Animated dot showing agent is active
 * ============================================================================
 *
 * USE CASE: Show a pulsing indicator when an agent is actively working
 *
 * IMPLEMENTATION:
 * ```tsx
 * import { agentStyles } from './theme';
 *
 * // In your component
 * <Box
 *   sx={{
 *     width: 8,
 *     height: 8,
 *     borderRadius: '50%',
 *     backgroundColor: 'primary.main',
 *     ...agentStyles.agentPulse, // Apply pulse animation
 *   }}
 * />
 * ```
 *
 * WHERE TO USE:
 * - Next to agent name in sidebar when agent is working
 * - In status badges showing "Agent Processing"
 * - In the top bar agent activity indicator
 *
 * ============================================================================
 * 2. AGENT SHIMMER - Animated shimmer effect for processing cards
 * ============================================================================
 *
 * USE CASE: Show a card is being processed by an AI agent
 *
 * IMPLEMENTATION:
 * ```tsx
 * import { agentStyles } from './theme';
 *
 * // Apply to Card or Paper component
 * <Card
 *   sx={{
 *     ...agentStyles.agentShimmer, // Apply shimmer effect
 *     position: 'relative',
 *     overflow: 'hidden',
 *   }}
 * >
 *   <CardContent>Company metrics being extracted...</CardContent>
 * </Card>
 * ```
 *
 * WHERE TO USE:
 * - Portfolio company cards while data is being extracted
 * - Submission cards while agent is processing documents
 * - Any card representing work in progress
 *
 * ============================================================================
 * 3. AGENT AVATAR - Consistent avatar styling for AI agents
 * ============================================================================
 *
 * USE CASE: Display agent identity with consistent branding
 *
 * IMPLEMENTATION:
 * ```tsx
 * import { Avatar } from '@mui/material';
 * import { agentStyles } from './theme';
 *
 * // Pass current theme mode
 * const mode = theme.palette.mode; // 'light' or 'dark'
 *
 * <Avatar sx={agentStyles.agentAvatar(mode)}>
 *   DA
 * </Avatar>
 * // Note: DA = Data Analyst Agent initials
 * ```
 *
 * WHERE TO USE:
 * - Sidebar navigation for agent sections
 * - Agent activity feed items
 * - Agent selection dropdowns
 * - Agent status cards
 *
 * AGENT INITIALS:
 * - DA: Data Analyst Agent
 * - [Add more as you create new agents]
 *
 * ============================================================================
 * 4. STATUS COLORS - Semantic colors for agent states
 * ============================================================================
 *
 * USE CASE: Consistent coloring for different agent statuses
 *
 * IMPLEMENTATION:
 * ```tsx
 * import { Chip } from '@mui/material';
 * import { agentStyles } from './theme';
 *
 * // For success/completed state
 * <Chip
 *   label="Complete"
 *   sx={{ backgroundColor: agentStyles.statusColors.success }}
 * />
 *
 * // For processing state
 * <Chip
 *   label="Processing"
 *   sx={{ backgroundColor: agentStyles.statusColors.processing }}
 * />
 *
 * // For warning state
 * <Chip
 *   label="Needs Review"
 *   sx={{ backgroundColor: agentStyles.statusColors.warning }}
 * />
 *
 * // For error state
 * <Chip
 *   label="Failed"
 *   sx={{ backgroundColor: agentStyles.statusColors.error }}
 * />
 * ```
 *
 * WHERE TO USE:
 * - Status badges on submission cards
 * - Agent job status indicators
 * - Progress indicators
 * - Alert messages
 *
 * STATUS DEFINITIONS:
 * - success: Agent completed task successfully (Teal)
 * - processing: Agent is actively working (Teal with pulse)
 * - warning: Agent needs human input/review (Amber)
 * - error: Agent encountered an error (Rose)
 *
 * ============================================================================
 * COMPLETE EXAMPLE - Agent Activity Card
 * ============================================================================
 *
 * ```tsx
 * import { Card, CardContent, Avatar, Box, Chip, Typography } from '@mui/material';
 * import { agentStyles, colors } from './theme';
 * import { useTheme } from '@mui/material/styles';
 *
 * function AgentActivityCard({ agent, status, message }) {
 *   const theme = useTheme();
 *   const isProcessing = status === 'processing';
 *
 *   return (
 *     <Card sx={isProcessing ? agentStyles.agentShimmer : {}}>
 *       <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
 *         // Agent avatar
 *         <Avatar sx={agentStyles.agentAvatar(theme.palette.mode)}>
 *           DA
 *         </Avatar>
 *
 *         <Box sx={{ flex: 1 }}>
 *           <Typography variant="subtitle1">Data Analyst</Typography>
 *           <Typography variant="body2" color="text.secondary">
 *             {message}
 *           </Typography>
 *         </Box>
 *
 *         // Status indicator with pulse
 *         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
 *           {isProcessing && (
 *             <Box
 *               sx={{
 *                 width: 8,
 *                 height: 8,
 *                 borderRadius: '50%',
 *                 backgroundColor: agentStyles.statusColors.processing,
 *                 ...agentStyles.agentPulse,
 *               }}
 *             />
 *           )}
 *           <Chip
 *             label={status}
 *             size="small"
 *             sx={{
 *               backgroundColor: agentStyles.statusColors[status],
 *               color: 'white',
 *             }}
 *           />
 *         </Box>
 *       </CardContent>
 *     </Card>
 *   );
 * }
 * ```
 *
 * ============================================================================
 */
export const agentStyles = {
  /**
   * Pulsing animation for active agent indicators
   * Apply to small circular elements (8x8px dots)
   */
  agentPulse: {
    '@keyframes pulse': {
      '0%, 100%': {
        opacity: 1,
      },
      '50%': {
        opacity: 0.5,
      },
    },
    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
  },

  /**
   * Shimmer effect for cards being processed by agents
   * Apply to Card or Paper components with position: 'relative' and overflow: 'hidden'
   */
  agentShimmer: {
    '@keyframes shimmer': {
      '0%': {
        backgroundPosition: '-1000px 0',
      },
      '100%': {
        backgroundPosition: '1000px 0',
      },
    },
    background: `linear-gradient(to right, transparent 0%, ${alpha(colors.green[500], 0.1)} 50%, transparent 100%)`,
    backgroundSize: '1000px 100%',
    animation: 'shimmer 2s infinite',
  },

  /**
   * Consistent avatar styling for AI agents
   * Pass theme mode ('light' or 'dark') to get appropriate colors
   *
   * @param mode - 'light' or 'dark' from theme.palette.mode
   * @returns SxProps for Avatar component
   */
  agentAvatar: (mode: 'light' | 'dark') => ({
    width: 32,
    height: 32,
    backgroundColor: mode === 'light' ? colors.green[700] : colors.green[500],
    color: mode === 'light' ? '#FFFFFF' : colors.slate[900],
    fontWeight: 600,
    fontSize: '0.875rem',
  }),

  /**
   * Semantic colors for agent status states
   * Use these for Chips, badges, and status indicators
   *
   * - success: Task completed successfully
   * - processing: Agent actively working (combine with agentPulse)
   * - warning: Needs human review/input
   * - error: Task failed or encountered error
   */
  statusColors: {
    success: colors.emerald[500],
    warning: colors.amber[500],
    error: colors.rose[500],
    processing: colors.green[500],
  },
};

/**
 * Export color palette for direct component usage
 *
 * USAGE:
 * ```tsx
 * import { colors } from './theme';
 *
 * <Box sx={{
 *   backgroundColor: colors.green[50],
 *   borderColor: colors.green[700]
 * }} />
 * ```
 */
export { colors };
